import clsx from "clsx";
import { useEffect, useId, useRef } from "react";

import { Icon } from "../Icon";
import { Popover } from "../Popover";

export type FilterMultiSelectOption = Readonly<{
  label: string;
  value: string;
}>;

interface FilterMultiSelectProps {
  options: readonly FilterMultiSelectOption[];
  value: readonly string[];
  onChange: (value: string[]) => void;
  allOptionsLabel?: string;
  multipleOptionsLabel?: string;
  compact?: boolean;
  disabled?: boolean;
  allowMultipleSelections?: boolean;
  labelledBy?: string;
}

interface IndeterminateCheckboxProps {
  checked: boolean;
  disabled: boolean;
  indeterminate: boolean;
  label: string;
  onChange: () => void;
}

const IndeterminateCheckbox = ({
  checked,
  disabled,
  indeterminate,
  label,
  onChange,
}: IndeterminateCheckboxProps): JSX.Element => {
  const inputElementReference = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (inputElementReference.current) {
      inputElementReference.current.indeterminate = indeterminate;
    }
  }, [indeterminate]);

  return (
    <input
      ref={inputElementReference}
      aria-checked={indeterminate ? "mixed" : checked}
      aria-label={label}
      type="checkbox"
      checked={checked}
      disabled={disabled}
      onChange={onChange}
      className={clsx(
        "h-5 w-5 shrink-0 appearance-none rounded-sm border-2 border-input-checked transition-colors focus-visible:outline focus-visible:outline-2",
        {
          "bg-white": !checked && !indeterminate,
          "bg-icon-check bg-input-checked bg-[length:14px_9px] bg-center bg-no-repeat": checked,
          "bg-icon-indeterminate bg-input-checked bg-[length:14px_9px] bg-center bg-no-repeat": indeterminate,
          "opacity-50": disabled,
        },
      )}
    />
  );
};

export const FilterMultiSelect = ({
  options,
  value,
  onChange,
  allOptionsLabel = "Todas as opções",
  multipleOptionsLabel = "N filtros aplicados",
  compact = false,
  disabled = false,
  allowMultipleSelections = true,
  labelledBy,
}: FilterMultiSelectProps): JSX.Element | null => {
  const actionId = useId();

  if (!options.length) {
    return null;
  }

  const optionValues = options.map((option) => option.value);
  const selectedValues = optionValues.filter((optionValue) => value.includes(optionValue));
  const allSelected = selectedValues.length === optionValues.length;
  const hasPartialSelection = !allSelected && selectedValues.length > 0;
  let triggerLabel = allOptionsLabel;

  if (allowMultipleSelections) {
    triggerLabel = multipleOptionsLabel.replace("N", String(selectedValues.length));

    if (allSelected) {
      triggerLabel = allOptionsLabel;
    }
  }

  if (selectedValues.length === 1) {
    triggerLabel = options.find((option) => option.value === selectedValues[0])?.label ?? "";
  }

  const toggleAll = () => onChange(allSelected ? [] : optionValues);
  const toggleOption = (optionValue: string) => {
    const isSelected = selectedValues.includes(optionValue);

    if (!allowMultipleSelections) {
      onChange(isSelected ? [] : [optionValue]);
      return;
    }

    const nextSelectedValues = isSelected
      ? selectedValues.filter((selectedValue) => selectedValue !== optionValue)
      : [...selectedValues, optionValue];

    onChange(optionValues.filter((value) => nextSelectedValues.includes(value)));
  };

  return (
    <Popover
      disabled={disabled}
      placement="bottom-start"
      accessibleContent
      contentClassName="min-w-[16rem] gap-0 overflow-hidden !rounded-none border border-input-border bg-white p-0 shadow-box-primary"
      actionButtonProps={{
        "aria-label": compact && !labelledBy ? "Filtrar" : undefined,
        "aria-labelledby": compact && labelledBy ? `${actionId} ${labelledBy}` : undefined,
        className: clsx("text-body", {
          "gap-2 whitespace-nowrap rounded-sm border border-input-border bg-white px-4 py-2": !compact,
          "!min-h-[auto] !p-0 !w-fit": compact,
        }),
        children: compact ? (
          <>
            {labelledBy && (
              <span id={actionId} className="sr-only">
                Filtrar por
              </span>
            )}
            <Icon name="Filter" />
          </>
        ) : (
          <>
            <Icon name="Filter" />
            <span>{triggerLabel}</span>
            <Icon name="KeyboardArrowDown" />
          </>
        ),
      }}
    >
      <div className="flex flex-col py-1">
        {allowMultipleSelections && (
          <label className="flex min-h-11 w-full cursor-pointer items-center justify-between gap-4 px-4 py-2 text-left text-body hover:bg-input-selected-background">
            <span>{allOptionsLabel}</span>
            <IndeterminateCheckbox
              checked={allSelected}
              disabled={disabled}
              indeterminate={hasPartialSelection}
              label={allOptionsLabel}
              onChange={toggleAll}
            />
          </label>
        )}

        {options.map((option) => {
          const isSelected = selectedValues.includes(option.value);
          return (
            <label
              key={option.value}
              className="flex min-h-11 w-full cursor-pointer items-center justify-between gap-4 px-4 py-2 text-left text-body hover:bg-input-selected-background"
            >
              <span>{option.label}</span>
              <IndeterminateCheckbox
                checked={isSelected}
                disabled={disabled}
                indeterminate={false}
                label={option.label}
                onChange={() => toggleOption(option.value)}
              />
            </label>
          );
        })}
      </div>
    </Popover>
  );
};
